/**
 * Is this browser's encrypted work readable right now? One answer for the whole app, so a locked
 * browser says "locked" everywhere instead of "0 projects" (the UI never presents a failed
 * decrypt as an empty account).
 *
 * - `unlocked`: the current epoch key is held (or the account has no encrypted directory yet).
 * - `locked`: signed in, but this browser cannot read the current epoch: never trusted for content
 *   here, or an escrow-reader browser left on an old epoch by a rotation elsewhere (revoking a
 *   device, a new recovery code, a kit rotation). One passkey tap (PRF) unlocks it in place.
 * - `unknown`: not checked yet, or the check failed for another reason (offline): never shown as
 *   locked.
 */
import type { BrowserContentSession } from './content-session.js'
import type { FaceIdContent } from './faceid-signin.js'
import { passkeyErrorMessage } from './passkeys.js'
import type { ContentUnlockResult, MachineFailure, UnlockPhase } from './unlock-ceremony.js'

/** Errors that mean "no key here", as opposed to network or auth trouble. */
const LOCK_ERROR =
  /content locked|encrypted (entities|runs|history|artifacts|content) locked|private work is locked|locally trusted passkey|locally approved passkey|unapproved content device|approved key transfer|recover access to continue|(unapproved|unproven) wrapping recipient/i

export function isContentLockError(e: unknown): boolean {
  return e instanceof Error && LOCK_ERROR.test(e.message)
}

/** Read-only, no ceremony. Throws when it cannot tell (network, signed out). */
export async function probeContent(session: BrowserContentSession): Promise<'unlocked' | 'locked'> {
  try {
    await session.activeKey()
    return 'unlocked'
  } catch (e) {
    if (!isContentLockError(e)) throw e
  }
  // A fresh account (no signed directory yet) has nothing to unlock.
  try {
    if (await session.unprovisioned(await session.accountId())) return 'unlocked'
  } catch (e) {
    if (!isContentLockError(e)) throw e
  }
  return 'locked'
}

export type UnlockOutcome = FaceIdContent

export interface UnlockMachineRef {
  machineId: string
  name: string
}

/** A key-holding machine with its live state (hub `unlock/holders`), online or not. */
export interface UnlockHolderRef extends UnlockMachineRef {
  online: boolean
  /** The hub's last sign of life from it (ms). */
  lastSeenAt?: number
}

export type ContentLockState =
  | { status: 'unknown' }
  | {
      status: 'unlocked'
      /**
       * The last unlock needed a machine (the passkey couldn't open the keys itself): offer once
       * to set up the passkey (Face ID) unlock so it doesn't recur.
       */
      offerProtect?: boolean
    }
  | {
      status: 'unlocking'
      /** Waiting for the passkey, then for the key-holding machine (`machineName`). */
      phase?: 'passkey' | 'machine'
      machineName?: string
    }
  | {
      status: 'locked'
      /** The last ceremony could not open the passkey's escrow (no PRF / no escrow / still locked). */
      fallback?: 'no-prf' | 'no-escrow' | 'still-locked'
      /** Why the machine path did not unlock (Mac offline, expired, recovery hold, refused). */
      failure?: MachineFailure
      error?: string
      /** Online machines that hold the key: "Unlock via Areks-Macbook-Pro.local". */
      machines?: UnlockMachineRef[]
      /** Every key-holding machine with live state ("online" / "offline · last seen 11 h ago"). */
      holders?: UnlockHolderRef[]
      /** This browser's passkey gave no PRF secret last time: the machine path is the default. */
      prfUnavailable?: boolean
    }

export interface ContentLockSource {
  probe(): Promise<'unlocked' | 'locked'>
  /**
   * The ONE passkey ceremony (lib/unlock-ceremony.ts). Resolves once the passkey verified (the
   * app may unlock); `content` settles the content unlock (PRF locally, else via a machine).
   */
  ceremony(onPhase: (p: UnlockPhase) => void): Promise<{ content: Promise<ContentUnlockResult> }>
  /** Online key-holding machines (best effort). */
  machines?(): Promise<UnlockMachineRef[]>
  /** Every key-holding machine, online or not (best effort; older hubs: `machines` only). */
  holders?(): Promise<UnlockHolderRef[]>
  /** Set up the passkey (PRF) unlock on this browser, once unlocked ("Protect with passkey"). */
  protect?(): Promise<{ supported: boolean }>
}

const OFFERED = 'viko.offer-protect.done'
function offeredHere(): boolean {
  try {
    return globalThis.localStorage?.getItem(OFFERED) === '1'
  } catch {
    return false
  }
}
function rememberOffered() {
  try {
    globalThis.localStorage?.setItem(OFFERED, '1')
  } catch {
    // private mode: the offer may show again next time
  }
}

const PRF_NONE = 'viko.prf.none'
function prfUnavailableHere(): boolean {
  try {
    return globalThis.localStorage?.getItem(PRF_NONE) === '1'
  } catch {
    return false
  }
}
function rememberPrf(available: boolean) {
  try {
    if (available) globalThis.localStorage?.removeItem(PRF_NONE)
    else globalThis.localStorage?.setItem(PRF_NONE, '1')
  } catch {
    // private mode: the panel still works, it just doesn't remember
  }
}

export class ContentLock {
  state: ContentLockState = { status: 'unknown' }
  private readonly listeners = new Set<() => void>()
  private checking: Promise<void> | undefined
  private running: Promise<ContentUnlockResult> | undefined
  constructor(private readonly source: ContentLockSource | undefined) {
    if (!source) this.state = { status: 'unlocked' }
  }
  subscribe = (listener: () => void) => {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }
  get = () => this.state
  private set(next: ContentLockState) {
    if (JSON.stringify(next) === JSON.stringify(this.state)) return
    this.state = next
    for (const l of this.listeners) l()
  }
  private lockedState(extra: Partial<Extract<ContentLockState, { status: 'locked' }>> = {}) {
    const prev = this.state.status === 'locked' ? this.state : undefined
    const prfUnavailable = prfUnavailableHere()
    return {
      status: 'locked' as const,
      ...(prev?.machines ? { machines: prev.machines } : {}),
      ...(prev?.holders ? { holders: prev.holders } : {}),
      ...(prfUnavailable ? { prfUnavailable } : {}),
      ...extra,
    }
  }
  /** One request: `holders` when the hub has it, else the online-only `machines` list. */
  private async refreshMachines() {
    const source = this.source
    if (!source) return
    const holders = source.holders ? await source.holders().catch(() => undefined) : undefined
    if (holders) {
      if (this.state.status !== 'locked') return
      const sorted = [...holders].sort(
        (a, b) => Number(b.online) - Number(a.online) || (b.lastSeenAt ?? 0) - (a.lastSeenAt ?? 0),
      )
      this.set({
        ...this.state,
        holders: sorted.slice(0, 4),
        machines: sorted
          .filter((h) => h.online)
          .slice(0, 4)
          .map((h) => ({ machineId: h.machineId, name: h.name })),
      })
      return
    }
    const machines = await source.machines?.().catch(() => undefined)
    if (machines && this.state.status === 'locked')
      this.set({
        ...this.state,
        machines: machines.slice(0, 4),
        holders: machines.slice(0, 4).map((m) => ({ ...m, online: true })),
      })
  }
  /** The once-only "Unlock with Face ID next time" offer was answered (or set up). */
  dismissOffer() {
    rememberOffered()
    if (this.state.status === 'unlocked' && this.state.offerProtect)
      this.set({ status: 'unlocked' })
  }
  /** "Unlock with Face ID next time": the passkey (PRF) unlock for this browser. */
  async protect(): Promise<{ supported: boolean }> {
    const r = (await this.source?.protect?.()) ?? { supported: false }
    if (r.supported) this.dismissOffer()
    return r
  }
  /** Re-check (coalesced). Keeps a shown fallback/failure while still locked. */
  check(): Promise<void> {
    if (!this.source) return Promise.resolve()
    if (this.checking) return this.checking
    if (this.running) return this.running.then(() => {})
    const source = this.source
    this.checking = (async () => {
      try {
        const status = await source.probe()
        if (status === 'unlocked') {
          if (this.state.status !== 'unlocked') this.set({ status })
        } else {
          if (this.state.status !== 'locked') this.set(this.lockedState())
          else if (prfUnavailableHere() && !this.state.prfUnavailable)
            this.set({ ...this.state, prfUnavailable: true })
          await this.refreshMachines()
        }
      } catch {
        if (this.state.status !== 'locked') this.set({ status: 'unknown' })
      } finally {
        this.checking = undefined
      }
    })()
    return this.checking
  }
  /** Follow a content unlock already in flight (the ceremony's second half, or sign-in's). */
  track(content: Promise<ContentUnlockResult>): Promise<ContentUnlockResult> {
    const run = content.then(
      (r) => {
        rememberPrf(r.prf)
        if (r.status === 'unlocked')
          this.set(
            r.via === 'machine' && this.source?.protect && !offeredHere()
              ? { status: 'unlocked', offerProtect: true }
              : { status: 'unlocked' },
          )
        else
          this.set(
            this.lockedState({
              ...(r.fallback ? { fallback: r.fallback } : {}),
              ...(r.failure ? { failure: r.failure } : {}),
              ...(!r.prf || prfUnavailableHere() ? { prfUnavailable: true } : {}),
            }),
          )
        return r
      },
      (e) => {
        this.set(this.lockedState({ error: passkeyErrorMessage(e) }))
        throw e
      },
    )
    this.running = run
    void run
      .catch(() => {})
      .finally(() => {
        if (this.running === run) this.running = undefined
        if (this.state.status === 'locked') void this.refreshMachines()
      })
    return run
  }
  private onPhase = (p: UnlockPhase) => {
    // An app-lock tap while content state is unknown stays silent until a machine is involved.
    if (p.step === 'passkey' && this.state.status !== 'locked' && this.state.status !== 'unlocking')
      return
    this.set(
      p.step === 'machine'
        ? { status: 'unlocking', phase: 'machine', machineName: p.machineName }
        : { status: 'unlocking', phase: 'passkey' },
    )
  }
  /**
   * The panel's Unlock (and ⇧U): ONE ceremony that unlocks content by PRF or via a machine.
   * Resolves with the result; throws (and stays locked) on a cancelled tap.
   */
  async unlock(): Promise<ContentUnlockResult | undefined> {
    if (!this.source) return undefined
    if (this.running) return this.running
    this.set({ status: 'unlocking', phase: 'passkey' })
    let started: { content: Promise<ContentUnlockResult> }
    try {
      started = await this.source.ceremony(this.onPhase)
    } catch (e) {
      this.set(this.lockedState({ error: passkeyErrorMessage(e) }))
      throw e
    }
    return this.track(started.content)
  }
  /**
   * The app lock's unlock (AppLock): while content is locked (or not checked yet), the app lock's
   * passkey tap IS the content unlock ceremony, so there is never a second tap. Resolves once the
   * passkey verified (the app unlocks); the content unlock continues in the background with
   * inline progress. Otherwise `plain` (the app lock's own ceremony) runs.
   */
  async appUnlock(plain: () => Promise<void>): Promise<void> {
    const s = this.state.status
    if (!this.source || (s !== 'locked' && s !== 'unknown')) return plain()
    if (this.running) return plain()
    const before = this.state
    let started: { content: Promise<ContentUnlockResult> }
    try {
      started = await this.source.ceremony(this.onPhase)
    } catch (e) {
      this.set(before)
      throw e
    }
    void this.track(started.content).catch(() => {})
  }
}
